import { localNow } from './emitter.ts';

/**
 * NTP-style estimate of the offset between this machine's monotonic clock and the server's.
 * Every participant maps their recording timestamps onto the server clock, which is what lets
 * files from different machines line up on one timeline.
 */
export class ServerClock {
  private samples: { offset: number; rtt: number; at: number }[] = [];
  offset = 0;
  bestRtt = Infinity;
  synced = false;

  addSample(clientSent: number, serverTime: number, clientReceived = localNow()) {
    const rtt = clientReceived - clientSent;
    if (rtt < 0 || rtt > 10_000) return;
    // Assume the reply took half the round trip; error is bounded by rtt/2.
    const offset = serverTime - (clientSent + rtt / 2);
    this.samples.push({ offset, rtt, at: clientReceived });
    if (this.samples.length > 30) this.samples.shift();
    // The fastest exchanges have the least queuing delay, so trust them most.
    const best = [...this.samples].sort((a, b) => a.rtt - b.rtt).slice(0, 5);
    best.sort((a, b) => a.offset - b.offset);
    this.offset = best[Math.floor(best.length / 2)].offset;
    this.bestRtt = best[0].rtt;
    this.synced = this.samples.length >= 3;
  }

  /** Half the best round-trip: the worst-case error of `offset`. */
  get uncertaintyMs() {
    return Number.isFinite(this.bestRtt) ? this.bestRtt / 2 : 1000;
  }

  serverNow() {
    return localNow() + this.offset;
  }

  toServer(localMs: number) {
    return localMs + this.offset;
  }

  toLocal(serverMs: number) {
    return serverMs - this.offset;
  }
}
